import { useState } from "react";
import { Link } from "react-router-dom";
import { Play } from "lucide-react";
import { embedUrl } from "@/lib/format";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const withAutoplay = (src) => `${src}${src.includes("?") ? "&" : "?"}autoplay=1`;
export const isVertical = (item) => (item?.format || []).some((f) => f.includes("9:16"));

export const VideoPreview = ({ url, poster, title, autoplay = false }) => {
  const [active, setActive] = useState(autoplay);
  const e = embedUrl(url);
  if (!e) return poster ? <img src={poster} alt={title} loading="lazy" className="w-full h-full object-cover" /> : null;
  if (!active || e.type === "link") {
    const Btn = e.type === "link" ? "a" : "button";
    return (
      <Btn
        {...(e.type === "link" ? { href: e.src, target: "_blank", rel: "noopener noreferrer" } : { onClick: () => setActive(true), type: "button" })}
        className="relative block w-full h-full group" aria-label={`Play ${title}`} data-testid="video-preview-play"
      >
        {poster ? <img src={poster} alt={title} loading="lazy" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#121212]" />}
        <span className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-colors">
          <span className="w-16 h-16 flex items-center justify-center bg-[#0055FF] group-hover:bg-[#00E5FF] group-hover:text-black transition-colors"><Play size={22} /></span>
        </span>
      </Btn>
    );
  }
  if (e.type === "video") return <video src={e.src} controls autoPlay={autoplay} preload="none" poster={poster} className="w-full h-full object-contain bg-black" />;
  return <iframe src={autoplay ? withAutoplay(e.src) : e.src} title={title} className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen />;
};

export const VideoLightbox = ({ item, onClose }) => {
  const v = isVertical(item);
  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      {item && (
        <DialogContent className={`p-0 gap-0 border-[#27272A] bg-black rounded-none sm:rounded-none text-white [&>button]:text-white [&>button]:opacity-100 [&>button]:bg-black/70 [&>button]:p-2 [&>button]:z-10 ${v ? "max-w-[min(420px,92vw,calc(68vh*9/16))]" : "max-w-[min(1100px,94vw)]"}`} data-testid="video-lightbox">
          <div className={`${v ? "aspect-[9/16]" : "aspect-video"} w-full bg-black`} data-testid="video-lightbox-player">
            <VideoPreview url={item.video_url} poster={item.thumbnail_url} title={item.title} autoplay />
          </div>
          <div className="p-4 sm:p-5 border-t border-[#27272A] flex flex-wrap items-end justify-between gap-3">
            <div className="min-w-0">
              <p className="eb-label !text-[#00E5FF]">{item.category}</p>
              <DialogTitle className="mt-1 text-base sm:text-lg font-semibold leading-tight line-clamp-2" data-testid="video-lightbox-title">{item.title}</DialogTitle>
              <DialogDescription className="text-sm text-zinc-400">{item.client_name || "EditBay by Relatable Studio"}</DialogDescription>
            </div>
            <Link to="/enquire" onClick={onClose} className="eb-btn-primary" data-testid="video-lightbox-cta">Start a similar project</Link>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
};

export const PortfolioCard = ({ item, tall = false, onOpen }) => (
  <button type="button" onClick={() => onOpen(item)} className="group block w-full text-left" aria-label={`Play ${item.title}`} data-testid={`portfolio-card-${item.slug}`}>
    <div className={`relative overflow-hidden bg-[#121212] border border-[#27272A] ${isVertical(item) ? "aspect-[9/16]" : tall ? "aspect-[4/5]" : "aspect-video"}`}>
      {item.thumbnail_url ? (
        <img src={item.thumbnail_url} alt={item.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-zinc-700 font-display text-2xl">{item.category}</div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
      <span className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center bg-black/60 backdrop-blur group-hover:bg-[#0055FF] transition-colors"><Play size={14} /></span>
      <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-5 md:p-6">
        <p className="eb-label !text-zinc-400 hidden sm:block">{item.category}</p>
        <h3 className="mt-2 text-sm sm:text-xl md:text-2xl font-semibold leading-tight text-white group-hover:text-[#00E5FF] transition-colors">{item.title}</h3>
        {item.client_name && <p className="text-sm text-zinc-400 mt-1">{item.client_name}</p>}
      </div>
    </div>
  </button>
);
